import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type { ApproveVenueInput, PendingVenue } from "./types";
import { PENDING_VENUES_QUERY_KEY } from "./use-pending-venues";

interface ApproveVenueParams {
  readonly venueId: string;
  readonly input: ApproveVenueInput;
}

async function approveVenue({ venueId, input }: ApproveVenueParams): Promise<unknown> {
  return httpClient(`/api/admin/venues/${venueId}/approve`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useApproveVenue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveVenue,
    onMutate: async ({ venueId }) => {
      await queryClient.cancelQueries({ queryKey: PENDING_VENUES_QUERY_KEY });

      const previous = queryClient.getQueryData<readonly PendingVenue[]>(PENDING_VENUES_QUERY_KEY);

      // When old is undefined (query never loaded), the updater returns
      // undefined — TanStack Query v5 treats this as a no-op (cache
      // untouched). In the current UI the user cannot click approve
      // without the list being rendered, so old is always defined.
      // If a future entrypoint (bulk action, deep link) bypasses the
      // list, revisit this assumption. The ?. is defensive, not a
      // [] fallback.
      queryClient.setQueryData<readonly PendingVenue[]>(PENDING_VENUES_QUERY_KEY, (old) =>
        old?.filter((v) => v.id !== venueId),
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PENDING_VENUES_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PENDING_VENUES_QUERY_KEY });
    },
  });
}
